/* eslint complexity: ["error", 7] */
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import classNames from 'classnames';
import FlagDropdown from '../FlagDropdown/FlagDropdown';
import PhoneField from '../PhoneField/PhoneField';
import FlagData from './phoneNumberData.json';
import './InputTel.less';

class InputTel extends Component {
    static propTypes = {
        /** id attribute for form field */
        id: PropTypes.string.isRequired,
        /** The phone number to populate the input field */
        phoneNumber: PropTypes.string,
        /** The country code of the flag you want displayed initially. */
        countryCode: PropTypes.string,
        /** Boolean that allows consuming app to NOT display the Country Code dropdown. */
        showCountrySelect: PropTypes.bool,
        /** When populated with a string, this will turn the border red and display a validation error beneath the input. */
        errorMsg: PropTypes.string,
        /** Function that gets called when the user selects a new country from the dropdown. This function should set the value of countryCode in your store. */
        onCountryCodeChange: PropTypes.func,
        /**
         * Function that gets called every time the user types in the input field.
         *  This function should set the value of phoneNumber in your store. This
         *  callback provides the event object and a phoneObject that includes
         *  {raw: (string) unformatted phone number, formatted: (string) formatted phone number,
         *  phoneCode: (string) phone code, isValid: (boolean) whether the phone number is valid}.
         */
        onPhoneChange: PropTypes.func,
        /**
         * Function called when the input field gets focus. Callback signature:
         *  `function(event, phoneObject)` where phoneObject provides the current
         *  state of the phone field and has the same shape as the phoneObject
         *  received in the onPhoneChange handler.
         */
        onPhoneFocus: PropTypes.func,
        /**
         * Function called when the input field loses focus. Callback signature:
         *  `function(event, phoneObject)` where phoneObject provides the current
         *  state of the phone field and has the same shape as the phoneObject
         *  received in the onPhoneChange handler.
         */
        onPhoneBlur: PropTypes.func,
        /**
         * Function called when an error occurs displaying the specified data.
         * Currently, this is only invoked if the specified `countryCode` is not found.
         */
        onError: PropTypes.func,
        /** Translations for the component. */
        labels: PropTypes.shape({
            phoneNumber: PropTypes.string,
            countryCode: PropTypes.string
        }),
        /** Translations for the countries with the key as the country code and the value as the country name. */
        countryCodes: PropTypes.objectOf(PropTypes.string)
    }

    static defaultProps = {
        id: 'react-input-tel',
        phoneNumber: '',
        countryCode: 'us',
        showCountrySelect: true,
        errorMsg: '',
        onCountryCodeChange: () => {},
        onPhoneChange: () => {},
        onPhoneFocus: () => {},
        onPhoneBlur: () => {},
        onError: () => {},
        labels: {
            phoneNumber: 'Phone Number',
            countryCode: 'Country Code'
        },
        countryCodes: {}
    }

    constructor(props) {
        super(props);

        // create a shallow copy of each FlagData object for each instance of the input tel component
        this.flagData = FlagData.map((item) => {
            const {...obj} = item;
            return obj;
        });

        if (props.countryCodes) {
            this.mergeFlagData(props.countryCodes);
        }

        const countryData = this.getCountryData(props.countryCode);

        this.state = {
            countryCode: props.countryCode,
            phoneCode: countryData ? countryData.phoneCode : '1',
            phoneMasks: countryData ? countryData.masks : []
        };
    }

    componentWillReceiveProps(nextProps) {
        const didCountryCodePropsChange = this.compareCountryCodeProps(nextProps);
        const countryData = this.getCountryData(nextProps.countryCode);
        const phoneCode = countryData ? countryData.phoneCode : '1';

        if (didCountryCodePropsChange) {
            this.mergeFlagData(nextProps.countryCodes);
        }

        if (phoneCode !== this.state.phoneCode) {
            this.setState({
                phoneCode: countryData ? countryData.phoneCode : '1',
                phoneMasks: countryData ? countryData.masks : []
            });
        }
    }

    // check to see if any of the values in props.countryCodes have changed. If so, will have to re-merge this.flagData
    compareCountryCodeProps(nextProps) {
        for (const countryCode in nextProps.countryCodes) {
            if (Object.prototype.hasOwnProperty.call(nextProps.countryCodes, countryCode)) {
                const nextPropsCountryName = nextProps.countryCodes[countryCode];
                let currentFlagDataCountryName;

                if (nextPropsCountryName) {
                    let flagDataObjectIndex = this.findIndex(this.flagData, (obj) => {
                        return obj.cc === countryCode;
                    });

                    if (flagDataObjectIndex !== -1) {
                        currentFlagDataCountryName = this.flagData[flagDataObjectIndex].name;

                        if (nextPropsCountryName !== currentFlagDataCountryName) {
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }

    getCountryData(countryCode) {
        const countryData = this.find(this.flagData, (data) => data.cc === countryCode);
        if (!countryCode || !countryData) {
            this.props.onError(new Error('Invalid country code'));
            return this.find(this.flagData, (data) => data.cc === 'default');
        }
        return countryData;
    }

    handleCountryCodeChange = (e, countryData) => {
        if (countryData) {
            this.setState({
                phoneCode: countryData.phoneCode,
                phoneMasks: countryData.masks
            });
            this.phoneField.focus();
        }

        this.props.onCountryCodeChange(e, countryData);
    }

    // polyfill for Array.prototype.find()
    /* eslint-disable consistent-return */
    find(array, predicate, context) {
        context = context || this;
        let length = array.length;
        let i;

        for (i = 0; i < length; i++) {
            if (predicate.call(context, array[i], i, array)) {
                return array[i];
            }
        }
    }
    /* eslint-enable */

    // polyfill for .findIndex()
    findIndex(array, callback) {
        let length = array.length;
        let i;

        for (i = 0; i < length; i++) {
            if (callback(array[i])) {
                return i;
            }
        }
        return -1;
    }

    // find the object from this.flagData array that has the same country code as the country code passed in this.props.countryCodes
    // then replace the name value for the matching object with the name passed in in this.props.countryCodes
    mergeFlagData(countryCodes) {
        Object.keys(countryCodes).map((code) => {
            let matchingCountryObject = this.find(this.flagData, (data) => this.isMatchingCountryCode(data, code));

            if (matchingCountryObject) {
                matchingCountryObject.name = countryCodes[code];
            }
        });

        return this.sortCountries(this.flagData);
    }

    // sort the countries alphabetically by name
    sortCountries(flagData) {
        flagData.sort((a, b) => {
            let aName = a.name.toUpperCase();
            let bName = b.name.toUpperCase();
            if (aName < bName) {
                return -1;
            }
            if (aName > bName) {
                return 1;
            }
            return 0;
        });

        return flagData;
    }

    isMatchingCountryCode(data, code) {
        return data.cc === code.toString();
    }

    render() {
        const {
            id,
            phoneNumber,
            labels,
            errorMsg,
            onPhoneChange,
            onPhoneFocus,
            onPhoneBlur,
            showCountrySelect,
            countryCodes
        } = this.props;

        const containerClassName = classNames(this.props.className, {
            'react-input-tel': true,
            'input-group': true
        });

        return (
            <div className={containerClassName}>
                {showCountrySelect &&
                    <FlagDropdown
                        id={`${id}__dropdown`}
                        phoneCode={this.state.phoneCode}
                        countryCode={this.props.countryCode}
                        flagData={this.flagData}
                        onChange={this.handleCountryCodeChange}
                        countryCodes={countryCodes}
                        labels={labels}
                    />
                }
                <PhoneField
                    id={`${id}__input`}
                    phoneCode={this.state.phoneCode}
                    labels={labels}
                    masks={this.state.phoneMasks}
                    phoneNumber={phoneNumber}
                    errorMsg={errorMsg}
                    onChange={onPhoneChange}
                    onFocus={onPhoneFocus}
                    onBlur={onPhoneBlur}
                    showCountrySelect={showCountrySelect}
                    ref={(el) => {
                        /* istanbul ignore next */
                        this.phoneField = el;
                    }}
                />
            </div>
        );
    }
}

export default InputTel;
